import { useEffect, useRef } from "react";
import { useLocation } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export function VisitTracker() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const currentVisitId = useRef<string | null>(null);
  const enteredAt = useRef<number>(Date.now());

  const closeVisit = async () => {
    const id = currentVisitId.current;
    if (!id) return;
    const seconds = Math.max(0, Math.round((Date.now() - enteredAt.current) / 1000));
    currentVisitId.current = null;
    await supabase
      .from("page_visits")
      .update({ left_at: new Date().toISOString(), duration_seconds: seconds })
      .eq("id", id);
  };

  // Track route change
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const start = async () => {
      // close previous
      if (currentVisitId.current) {
        const id = currentVisitId.current;
        const seconds = Math.max(0, Math.round((Date.now() - enteredAt.current) / 1000));
        currentVisitId.current = null;
        void supabase
          .from("page_visits")
          .update({ left_at: new Date().toISOString(), duration_seconds: seconds })
          .eq("id", id);
      }

      enteredAt.current = Date.now();
      const { data, error } = await supabase
        .from("page_visits")
        .insert({ user_id: user.id, path: pathname, event_type: "visit" })
        .select("id")
        .single();
      if (!cancelled && !error && data) {
        currentVisitId.current = data.id;
      }
    };

    void start();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, pathname]);

  // Close on unload
  useEffect(() => {
    const handler = () => {
      const id = currentVisitId.current;
      if (!id) return;
      const seconds = Math.max(0, Math.round((Date.now() - enteredAt.current) / 1000));
      // Best-effort fire and forget
      void supabase
        .from("page_visits")
        .update({ left_at: new Date().toISOString(), duration_seconds: seconds })
        .eq("id", id);
    };
    window.addEventListener("beforeunload", handler);
    window.addEventListener("pagehide", handler);
    return () => {
      window.removeEventListener("beforeunload", handler);
      window.removeEventListener("pagehide", handler);
    };
  }, []);

  return null;
}
